/**
 * Universal Delete Slice
 * 
 * Handles all delete operations using the unified delete system.
 * Provides progress tracking, error handling, and standardized responses.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../apiTools/apiClient';
import {
  DELETE_ENTITY_TYPES,
  DELETE_BEHAVIORS,
  DELETE_RESPONSE_TYPES
} from '../constants/deleteConfig';

// Universal delete thunk
export const deleteEntity = createAsyncThunk(
  'delete/deleteEntity',
  async ({ entityType, entityId, options = {}, operationId }, { rejectWithValue }) => {
    try {
      const {
        cleanup = DELETE_BEHAVIORS.CLEANUP_S3,
        cascade = DELETE_BEHAVIORS.CASCADE_ALL,
        dryRun = false,
        force = false
      } = options;

      console.log(`[DELETE_ENTITY] Deleting ${entityType} ${entityId} with options:`, options);

      const params = new URLSearchParams({
        cleanup,
        cascade,
        dryRun: dryRun.toString(),
        force: force.toString()
      });

      const response = await apiClient.delete(
        `/delete/${entityType}/${entityId}?${params}`
      );

      console.log(`[DELETE_ENTITY] Response:`, response.data);

      return {
        ...response.data,
        entityType,
        entityId,
        operationId: operationId || `${entityType}-${entityId}-${Date.now()}`
      };
    } catch (error) {
      console.error(`[DELETE_ENTITY] Error:`, error);
      return rejectWithValue({
        entityType,
        entityId,
        operationId: operationId || `${entityType}-${entityId}-${Date.now()}`,
        error: error.response?.data || error.message || `Failed to delete ${entityType}`,
        details: error.response?.data || error
      });
    }
  }
);

// Initial state
const initialState = {
  operations: {},       // Track ongoing operations by operationId
  results: {},          // Completed operation results by operationId
  errors: {},           // Error states by operationId
  lastOperation: null   // Most recent operation for quick access
};

// Create the slice
const deleteSlice = createSlice({
  name: 'delete',
  initialState,
  reducers: {
    clearDeleteResult: (state, action) => {
      const operationId = action.payload;
      if (operationId) {
        delete state.results[operationId];
        delete state.errors[operationId];
        delete state.operations[operationId];
      }
    },
    clearAllDeleteResults: (state) => {
      state.operations = {};
      state.results = {};
      state.errors = {};
      state.lastOperation = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteEntity.pending, (state, action) => {
        const { entityType, entityId, operationId } = action.meta.arg;
        const opId = operationId || `${entityType}-${entityId}-${Date.now()}`;

        state.operations[opId] = {
          entityType,
          entityId,
          status: 'loading',
          startTime: Date.now()
        };
        state.lastOperation = opId;

        // Clear any previous errors for this operation
        delete state.errors[opId];
      })
      .addCase(deleteEntity.fulfilled, (state, action) => {
        const { operationId, entityType, entityId } = action.payload;

        // Move from operations to results
        delete state.operations[operationId];
        state.results[operationId] = {
          ...action.payload,
          completedAt: Date.now()
        };

        console.log(`[DELETE_SLICE] Delete completed for ${entityType} ${entityId}`);
      })
      .addCase(deleteEntity.rejected, (state, action) => {
        const { entityType, entityId, operationId } = action.payload || action.meta.arg;

        // Move from operations to errors
        delete state.operations[operationId];
        state.errors[operationId] = {
          entityType,
          entityId,
          error: action.payload?.error || action.error?.message || 'Delete failed',
          details: action.payload?.details || action.error,
          failedAt: Date.now()
        };

        console.error(`[DELETE_SLICE] Delete failed for ${entityType} ${entityId}:`, action.payload);
      });
  }
});

// Export actions
export const { clearDeleteResult, clearAllDeleteResults } = deleteSlice.actions;

// Selectors (updated for user.delete path)
export const selectDeleteOperation = (state, operationId) =>
  state.user.delete.operations[operationId];

export const selectDeleteResult = (state, operationId) =>
  state.user.delete.results[operationId];

export const selectDeleteError = (state, operationId) =>
  state.user.delete.errors[operationId];

export const selectLastDeleteOperation = (state) =>
  state.user.delete.lastOperation;

export const selectLastDeleteResult = (state) => {
  const lastOpId = state.user.delete.lastOperation;
  return lastOpId ? state.user.delete.results[lastOpId] : null;
};

export const selectIsDeleting = (state, entityType, entityId) => {
  return Object.values(state.user.delete.operations).some(op =>
    op.entityType === entityType && op.entityId === entityId
  );
};

export default deleteSlice.reducer;
